import React, {useState, useEffect, useCallback} from 'react'
import {useIntl} from 'react-intl'
import {useDispatch, useSelector} from 'react-redux'
import {contractAddendumGetAll, contractAddendumDelete} from '../../../redux/slices/project.slice'
import {DeleteModal} from '../../../components'
import {ContractAddendumModal} from './ContractAddendumModal'
import {KTSVG} from '../../../helpers'

const selectData = (state) => ({
  contractAddendums: state.project.contractAddendums,
  error: state.project.error,
})

function ContractAddendumList({contractId}) {
  const intl = useIntl()
  const dispatch = useDispatch()
  const {contractAddendums, error} = useSelector(selectData)

  const [showModal, setShowModal] = useState(false)
  const [editItem, setEditItem] = useState(null)
  const [showDelete, setShowDelete] = useState(false)
  const [deleteId, setDeleteId] = useState(null)

  const loadAddendums = useCallback(() => {
    dispatch(contractAddendumGetAll(contractId))
  }, [dispatch, contractId])

  useEffect(() => {
    loadAddendums()
  }, [loadAddendums])

  const handleAdd = useCallback(() => {
    setEditItem(null)
    setShowModal(true)
  }, [])

  const handleEdit = useCallback((item) => {
    setEditItem(item)
    setShowModal(true)
  }, [])

  const handleModalClose = useCallback((saved) => {
    setShowModal(false)
    setEditItem(null)
    if (saved) loadAddendums()
  }, [loadAddendums])

  const handleDeleteClick = useCallback((id) => {
    setDeleteId(id)
    setShowDelete(true)
  }, [])

  const handleDelete = useCallback(async () => {
    setShowDelete(false)
    await dispatch(contractAddendumDelete({id: deleteId}))
    loadAddendums()
  }, [dispatch, deleteId, loadAddendums])

  return (
    <div className='p-5'>
      <div className='d-flex justify-content-end mb-5'>
        <button type='button' className='btn btn-sm btn-primary' onClick={handleAdd}>
          <KTSVG path='/images/icons/duotune/arrows/arr075.svg' className='svg-icon-2' />
          {intl.formatMessage({id: 'CONTRACT.ADDENDUM.ADD'})}
        </button>
      </div>

      {(!contractAddendums || contractAddendums.length === 0) && (
        <div className='text-center text-muted py-5'>
          {intl.formatMessage({id: 'CONTRACT.ADDENDUM.EMPTY'})}
        </div>
      )}

      {contractAddendums && contractAddendums.length > 0 && (
        <div className='table-responsive'>
          <table className='table table-row-dashed table-row-gray-300 align-middle gs-0 gy-4'>
            <thead>
              <tr className='fw-bold text-muted'>
                <th>{intl.formatMessage({id: 'CONTRACT.ADDENDUM.ADDENDUMDATE'})}</th>
                <th>{intl.formatMessage({id: 'CONTRACT.ADDENDUM.ADDENDUMDESCRIPTION'})}</th>
                <th>{intl.formatMessage({id: 'CONTRACT.ADDENDUM.EXTENSIONDATE'})}</th>
                <th>{intl.formatMessage({id: 'CONTRACT.ADDENDUM.EXTENSIONDESCRIPTION'})}</th>
                <th className='text-end'></th>
              </tr>
            </thead>
            <tbody>
              {contractAddendums.map((item) => (
                <tr key={item.id}>
                  <td>
                    {item.addendumDate
                      ? intl.formatDate(new Date(item.addendumDate), {
                          year: 'numeric',
                          month: '2-digit',
                          day: '2-digit',
                        })
                      : '-'}
                  </td>
                  <td className='text-truncate' style={{maxWidth: 200}}>
                    {item.addendumDescription || '-'}
                  </td>
                  <td>
                    {item.extensionDate
                      ? intl.formatDate(new Date(item.extensionDate), {
                          year: 'numeric',
                          month: '2-digit',
                          day: '2-digit',
                        })
                      : '-'}
                  </td>
                  <td className='text-truncate' style={{maxWidth: 200}}>
                    {item.extensionDescription || '-'}
                  </td>
                  <td className='text-end'>
                    <button
                      type='button'
                      className='btn btn-icon btn-sm btn-light-primary me-2'
                      onClick={() => handleEdit(item)}
                      title={intl.formatMessage({id: 'BUTTON.EDIT'})}
                    >
                      <KTSVG
                        path='/images/icons/duotune/general/gen055.svg'
                        className='svg-icon-3'
                      />
                    </button>
                    <button
                      type='button'
                      className='btn btn-icon btn-sm btn-light-danger'
                      onClick={() => handleDeleteClick(item.id)}
                      title={intl.formatMessage({id: 'BUTTON.DELETE'})}
                    >
                      <KTSVG
                        path='/images/icons/duotune/general/gen027.svg'
                        className='svg-icon-3'
                      />
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <ContractAddendumModal
        show={showModal}
        onHide={handleModalClose}
        contractId={contractId}
        editItem={editItem}
      />

      <DeleteModal
        onClick={handleDelete}
        onOpen={showDelete}
        onClose={() => setShowDelete(false)}
        error={error}
      />
    </div>
  )
}

export {ContractAddendumList}
